import { ChangeDetectionStrategy, Component, computed, linkedSignal, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { IntegrationService, IntegrationTypes } from "../../integrations/integration.service";
import { DocusignApi } from "../../../services/docusign.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";

@TypeManagerDecorator("s25-ng-docusign-settings")
@Component({
    selector: "s25-ng-docusign-settings",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ToggleButtonModule],
    template: `
        <div class="c-margin-left--half">
            <h2 class="c-margin-bottom--single">DocuSign</h2>
            @if (connectionResource.isLoading()) {
                <p>Loading DocuSign settings…</p>
            } @else {
                <label class="c-displayBlock" for="docusign-sender-name">Sender name</label>
                <input
                    id="docusign-sender-name"
                    class="c-input c-margin-top--quarter"
                    [value]="senderName()"
                    (input)="onSenderName($event)"
                />

                <div class="c-margin-top--single">
                    <label class="c-displayBlock" for="docusign-connect-key">Connect key</label>
                    <div class="ngFinePrint c-margin-top--quarter">
                        DocuSign Admin, then Connect, then Connect Keys.
                    </div>
                    @if (showKeyField()) {
                        <input
                            id="docusign-connect-key"
                            class="c-input c-margin-top--quarter"
                            type="password"
                            [value]="hmacSecret()"
                            (input)="onHmac($event)"
                        />
                        @if (connection()?.hmacConfigured) {
                            <div class="ngFinePrint c-margin-top--quarter">
                                Leave this blank and save to keep the current key.
                            </div>
                        }
                    } @else {
                        <div class="c-margin-top--quarter">Saved. Hidden because it is a secret.</div>
                    }
                </div>

                <div class="c-margin-top--single">
                    @if (connection()?.hmacConfigured && !showKeyField()) {
                        <button
                            type="button"
                            class="aw-button aw-button--outline c-margin-right--quarter"
                            (click)="replaceKey()"
                        >
                            Replace key
                        </button>
                    }
                    @if (connection()?.hmacConfigured && showKeyField()) {
                        <button
                            type="button"
                            class="aw-button aw-button--outline c-margin-right--quarter"
                            (click)="keepSavedKey()"
                        >
                            Cancel
                        </button>
                    }
                    <button type="button" class="aw-button aw-button--primary" (click)="save()">Save</button>
                </div>

                <div class="c-margin-top--double">
                    <div class="ngBold">Campus account</div>
                    <div class="ngFinePrint c-margin-top--quarter c-margin-bottom--quarter">
                        @if (connection()?.connected) {
                            Connected to {{ connection()?.accountName || "DocuSign" }}.
                        } @else if (canConnect()) {
                            Sign in to the campus DocuSign account.
                        } @else {
                            Save a Connect key first.
                        }
                    </div>
                    <button
                        type="button"
                        class="aw-button aw-button--primary"
                        [disabled]="!canConnect()"
                        (click)="connect()"
                    >
                        Connect
                    </button>
                </div>

                <div class="c-margin-top--double">
                    <s25-toggle-button
                        [value]="enabled()"
                        (valueChange)="onEnable($event)"
                        [disabled]="!canEnable()"
                        toggleLabel="Enable DocuSign"
                        falseLabel="Disabled"
                        trueLabel="Enabled"
                    ></s25-toggle-button>
                </div>
                @if (error()) {
                    <div class="ngRed c-margin-top--single">{{ error() }}</div>
                }
            }
        </div>
    `,
})
export class S25DocusignSettingsComponent {
    readonly connectionResource = s25Resource({ loader: () => DocusignApi.connection() });
    readonly connection = this.connectionResource.value;
    readonly senderName = linkedSignal(() => this.connection()?.senderName || "25Live Contracts");
    readonly enabled = linkedSignal(() => !!this.connection()?.enabled);
    readonly hmacSecret = signal("");
    readonly replacingKey = signal(false);
    readonly error = signal("");
    readonly showKeyField = computed(() => !this.connection()?.hmacConfigured || this.replacingKey());
    readonly canConnect = computed(() => !!this.connection()?.hmacConfigured);

    canEnable(): boolean {
        return !!this.connection()?.connected && !!this.connection()?.hmacConfigured;
    }

    onSenderName(event: Event) {
        this.senderName.set((event.target as HTMLInputElement).value);
    }

    onHmac(event: Event) {
        this.hmacSecret.set((event.target as HTMLInputElement).value);
    }

    replaceKey() {
        this.error.set("");
        this.hmacSecret.set("");
        this.replacingKey.set(true);
    }

    keepSavedKey() {
        this.error.set("");
        this.hmacSecret.set("");
        this.replacingKey.set(false);
    }

    async connect() {
        this.error.set("");
        if (!this.canConnect()) {
            this.error.set("Save a Connect key before connecting.");
            return;
        }
        const [connection, error] = await S25Util.Maybe(DocusignApi.connect());
        if (error || !connection?.consentUrl) {
            this.error.set(S25Util.errorText(error, "DocuSign could not complete that request."));
            return;
        }
        TelemetryService.send("docusign", "connect");
        window.location.href = connection.consentUrl;
    }

    async save() {
        this.error.set("");
        const [, error] = await S25Util.Maybe(DocusignApi.saveSettings(this.senderName(), this.hmacSecret()));
        if (error) {
            this.error.set(S25Util.errorText(error, "DocuSign could not complete that request."));
            return;
        }
        this.hmacSecret.set("");
        this.replacingKey.set(false);
        await this.connectionResource.reloadAndAwait();
    }

    async onEnable(next: boolean) {
        const connection = this.connection();
        if (!connection?.integrationId) return;
        if (next && !this.canEnable()) {
            this.error.set("Save a Connect key and connect the campus account before turning DocuSign on.");
            this.enabled.set(false);
            return;
        }
        const [, error] = await S25Util.Maybe(
            IntegrationService.setEnabled(connection.integrationId, IntegrationTypes.docusign, next),
        );
        if (error) {
            this.error.set(S25Util.errorText(error, "DocuSign could not complete that request."));
            this.enabled.set(!!connection.enabled);
            return;
        }
        this.enabled.set(next);
        this.error.set("");
    }
}
